import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plane, ArrowRightLeft, Search, ChevronDown, Plus, Minus, CircleUser, UserRound, Baby } from 'lucide-react';
import popularAirportsData from '../data/popular_airports.json';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

// Lazy load full airports
let fullAirportsData = null;
const loadFullAirports = async () => {
  if (!fullAirportsData) {
    const module = await import('../data/airports.json');
    fullAirportsData = module.default;
  }
  return fullAirportsData;
};

const DesktopModifySearch = ({
  showModifySearch,
  setShowModifySearch,
  tripType,
  setTripType,
  fromAirport,
  setFromAirport,
  toAirport,
  setToAirport,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  adults,
  setAdults,
  children,
  setChildren,
  infants,
  setInfants,
  travelClass,
  setTravelClass,
  onSearch,
  showTripTypeSelector = false
}) => {
  // Local state for form fields (to prevent auto-search on every change)
  const [localFromAirport, setLocalFromAirport] = useState(fromAirport);
  const [localToAirport, setLocalToAirport] = useState(toAirport);
  const [localDepartureDate, setLocalDepartureDate] = useState(departureDate);
  const [localReturnDate, setLocalReturnDate] = useState(returnDate);
  const [localAdults, setLocalAdults] = useState(adults);
  const [localChildren, setLocalChildren] = useState(children);
  const [localInfants, setLocalInfants] = useState(infants);
  const [localTravelClass, setLocalTravelClass] = useState(travelClass);
  const [localTripType, setLocalTripType] = useState(tripType);

  // Popular airports for quick load — the AirportPicker below will lazy-load
  // the full airports.json (5379 entries) when the user starts typing, so the
  // dropdown matches the HomePage search behaviour instead of being limited
  // to popular cities only.
  const airports = popularAirportsData;

  // Sync local state with props when props change
  useEffect(() => {
    setLocalFromAirport(fromAirport);
    setLocalToAirport(toAirport);
    setLocalDepartureDate(departureDate);
    setLocalReturnDate(returnDate);
    setLocalAdults(adults);
    setLocalChildren(children);
    setLocalInfants(infants);
    setLocalTravelClass(travelClass);
    setLocalTripType(tripType);
  }, [fromAirport, toAirport, departureDate, returnDate, adults, children, infants, travelClass, tripType]);

  // Look up the selected airport's display info. Prefers the module-level
  // cache (populated once any AirportPicker has lazy-loaded the full list)
  // so rare airports render their city name after the first search. Falls
  // back to the popular-airports slice for the fast first paint.
  const _lookup = (code) => {
    if (!code) return undefined;
    if (fullAirportsData) {
      const hit = fullAirportsData.find((a) => a.code === code);
      if (hit) return hit;
    }
    return airports.find((a) => a.code === code);
  };
  const fromAirportInfo = _lookup(fromAirport);
  const toAirportInfo = _lookup(toAirport);

  const handleSwapAirports = () => {
    const temp = localFromAirport;
    setLocalFromAirport(localToAirport);
    setLocalToAirport(temp);
  };

  const handleSearch = () => {
    // Call onSearch with the updated local values
    if (onSearch) {
      onSearch({
        fromAirport: localFromAirport,
        toAirport: localToAirport,
        departureDate: localDepartureDate,
        returnDate: localReturnDate,
        adults: localAdults,
        children: localChildren,
        infants: localInfants,
        travelClass: localTravelClass,
        tripType: localTripType
      });
    }
    
    // Close modify search
    setShowModifySearch(false);
  };

  const handleCancel = () => {
    // Reset local state to original props
    setLocalFromAirport(fromAirport);
    setLocalToAirport(toAirport);
    setLocalDepartureDate(departureDate);
    setLocalReturnDate(returnDate);
    setLocalAdults(adults);
    setLocalChildren(children);
    setLocalInfants(infants);
    setLocalTravelClass(travelClass);
    setLocalTripType(tripType);
    setShowModifySearch(false);
  };

  // Airport Picker Component — lazy-loads the full airports.json on user
  // search so the dropdown surfaces the same 5,379-airport catalog as the
  // HomePage instead of being limited to the ~394 popular ones. Matches
  // HomePage's AirportPicker behaviour 1:1.
  const AirportPicker = ({ label, selectedCode, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [allAirports, setAllAirports] = useState(null);
    const inputRef = useRef(null);
    const ref = useRef(null);

    // Effective search catalog: full list once lazy-loaded, else popular only.
    const searchCatalog = allAirports || airports;
    const info = searchCatalog.find((a) => a.code === selectedCode) ||
                 airports.find((a) => a.code === selectedCode);

    // Debounced search with lazy loading — triggered by the first keystroke.
    const handleSearchChange = async (value) => {
      setSearch(value);
      if (value.length > 0 && !allAirports) {
        setIsSearching(true);
        try {
          const fullData = await loadFullAirports();
          setAllAirports(fullData);
        } catch (error) {
          // Silent — the popular list stays as the fallback catalog.
        } finally {
          setIsSearching(false);
        }
      }
    };

    // Filtered list, capped at 100 entries for render performance.
    const filtered = useMemo(() => {
      if (!search) {
        return airports.slice(0, 50);
      }
      const q = search.toLowerCase();
      const results = searchCatalog.filter(
        (a) =>
          a.city.toLowerCase().includes(q) ||
          a.code.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q),
      );
      return results.slice(0, 100);
    }, [search, searchCatalog]);

    useEffect(() => {
      if (open) setTimeout(() => inputRef.current?.focus(), 50);
    }, [open]);

    useEffect(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
      <div ref={ref} className="relative min-w-0">
        <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
        <button onClick={() => { setOpen(!open); setSearch(''); }} className="w-full text-left">
              <div className="text-left">
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
                <p className="text-lg font-extrabold text-[#1B375B] leading-tight">{selectedCode}</p>
                <p className="text-[10px] text-gray-400 truncate">{info?.city}</p>
              </div>
            </button>

        {open && (
          <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 z-50 overflow-hidden w-[310px] animate-fadeInUp">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="City, airport or code..."
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#659A9B] focus:bg-white transition-all"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#659A9B]"></div>
                  </div>
                )}
              </div>
              {!search && (
                <p className="text-[10px] text-gray-400 mt-1.5">Popular destinations</p>
              )}
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filtered.map((a) => (
                <button
                  key={a.code}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all hover:bg-[#659A9B]/5 ${a.code === selectedCode ? 'bg-[#659A9B]/10' : ''}`}
                  onClick={() => { onSelect(a.code); setOpen(false); setSearch(''); }}
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-[#1B375B]">{a.code}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-[#1B375B] truncate">{a.city}, {a.country}</p>
                    <p className="text-[10px] text-gray-400 truncate">{a.name}</p>
                  </div>
                  {a.code === selectedCode && (
                    <div className="w-5 h-5 rounded-full bg-[#659A9B] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                </button>
              ))}
              {filtered.length === 0 && <p className="text-center py-4 text-sm text-gray-400">No airports found</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Date Picker Component
  const DatePicker = ({ label, date, onSelect, placeholder, minDate }) => {
    const [open, setOpen] = useState(false);
    const selected = date ? new Date(date) : undefined;

    const handleSelect = (day) => {
      if (day) {
        const yyyy = day.getFullYear();
        const mm = String(day.getMonth() + 1).padStart(2, '0');
        const dd = String(day.getDate()).padStart(2, '0');
        onSelect(`${yyyy}-${mm}-${dd}`);
        setOpen(false);
      }
    };

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="w-full text-left">
            <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
            {selected ? (
              <>
                <p className="text-base font-bold text-[#1B375B]">
                  {String(selected.getDate()).padStart(2, '0')} {format(selected, "MMM''yy")}
                </p>
                <p className="text-[10px] text-gray-400">{days[selected.getDay()]}</p>
              </>
            ) : (
              <p className="text-sm font-medium text-[#659A9B]">{placeholder || 'Select'}</p>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border-0" align="start" sideOffset={8}>
          <CalendarComponent
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            disabled={(d) => minDate ? d < new Date(minDate) : d < new Date(new Date().setHours(0,0,0,0))}
            initialFocus
            className="rounded-2xl"
            classNames={{
              day_selected: "bg-[#659A9B] text-white hover:bg-[#335E77] hover:text-white focus:bg-[#335E77] focus:text-white",
              day_today: "bg-[#659A9B]/10 text-[#1B375B] font-bold",
            }}
          />
        </PopoverContent>
      </Popover>
    );
  };

  // Passenger Selector Component
  const PassengerSelector = ({ adults: adultCount, children: childCount, infants: infantCount, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const total = adultCount + childCount + infantCount;

    useEffect(() => {
      const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    const Counter = ({ label, sublabel, icon, value, onInc, onDec, min = 0, max = 9 }) => (
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#659A9B]/10 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1B375B]">{label}</p>
            <p className="text-[10px] text-gray-400">{sublabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={onDec}
            disabled={value <= min}
            className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#659A9B] hover:text-[#659A9B] transition-colors text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="text-lg font-bold text-[#1B375B] w-6 text-center">{value}</span>
          <button
            onClick={onInc}
            disabled={value >= max}
            className="w-8 h-8 rounded-full border-2 border-[#659A9B] text-[#659A9B] flex items-center justify-center hover:bg-[#659A9B] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );

    return (
      <div ref={ref} className="relative">
        <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">Travellers</p>
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-left">
          <p className="text-base font-bold text-[#1B375B]">{String(total).padStart(2, '0')} {adultCount === 1 ? 'Adult' : 'Traveller'}</p>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 z-50 w-[300px] animate-fadeInUp">
            <div className="px-5 pt-4 pb-1">
              <p className="text-xs font-bold text-[#1B375B] uppercase tracking-wider mb-1">Passengers</p>
            </div>
            <div className="px-5 divide-y divide-gray-100">
              <Counter
                label="Adults"
                sublabel="12+ years"
                icon={<CircleUser className="w-4 h-4 text-[#659A9B]" />}
                value={adultCount}
                onInc={() => onChange({ adults: adultCount + 1, children: childCount, infants: infantCount })}
                onDec={() => onChange({ adults: adultCount - 1, children: childCount, infants: infantCount })}
                min={1}
                max={9}
              />
              <Counter
                label="Children"
                sublabel="2-12 years"
                icon={<UserRound className="w-4 h-4 text-[#659A9B]" />}
                value={childCount}
                onInc={() => onChange({ adults: adultCount, children: childCount + 1, infants: infantCount })}
                onDec={() => onChange({ adults: adultCount, children: childCount - 1, infants: infantCount })}
                min={0}
                max={9}
              />
              <Counter
                label="Infants"
                sublabel="Under 2 years"
                icon={<Baby className="w-4 h-4 text-[#659A9B]" />}
                value={infantCount}
                onInc={() => onChange({ adults: adultCount, children: childCount, infants: infantCount + 1 })}
                onDec={() => onChange({ adults: adultCount, children: childCount, infants: infantCount - 1 })}
                min={0}
                max={adultCount}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-100">
              <button
                onClick={() => setOpen(false)}
                className="w-full py-2.5 bg-[#1B375B] text-white rounded-xl text-sm font-bold hover:bg-[#659A9B] transition-all"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="hidden md:block max-w-7xl mx-auto px-4 pt-5 pb-0 relative z-30">
      {!showModifySearch ? (
        // COLLAPSED VIEW
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-4">
          <div className="flex items-center gap-0">
            {/* Trip Type Badge */}
            {showTripTypeSelector && (
              <div className="px-4 pr-5 border-r border-gray-100">
                <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                  localTripType === 'roundTrip' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {localTripType === 'roundTrip' ? '⇄ Round Trip' : '→ One Way'}
                </span>
              </div>
            )}

            {/* From */}
            <div className="flex items-center gap-2.5 px-4 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-[#659A9B]/10 flex items-center justify-center flex-shrink-0">
                <Plane className="w-4 h-4 text-[#659A9B]" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">From</p>
                <p className="text-lg font-extrabold text-[#1B375B] leading-tight">{fromAirport}</p>
                <p className="text-[10px] text-gray-400 truncate">{fromAirportInfo?.city}</p>
              </div>
            </div>

            {/* Swap icon */}
            <div className="w-9 h-9 rounded-full bg-[#E7EEEE] flex items-center justify-center flex-shrink-0 mx-1">
              <ArrowRightLeft className="w-4 h-4 text-[#659A9B]" />
            </div>

            {/* To */}
            <div className="flex items-center gap-2.5 px-4 flex-1 min-w-0 border-r border-gray-100">
              <div className="min-w-0">
                <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">To</p>
                <p className="text-lg font-extrabold text-[#1B375B] leading-tight">{toAirport}</p>
                <p className="text-[10px] text-gray-400 truncate">{toAirportInfo?.city}</p>
              </div>
            </div>

            {/* Date */}
            <div className="px-5 border-r border-gray-100">
              <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">
                {localTripType === 'roundTrip' ? 'Departure - Return' : 'Departure'}
              </p>
              <p className="text-base font-bold text-[#1B375B]">
                {departureDate && new Date(departureDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).replace(/ /g, " ")}
                {localTripType === 'roundTrip' && returnDate && (
                  <> - {new Date(returnDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).replace(/ /g, " ")}</>
                )}
              </p>
              <p className="text-[10px] text-gray-400">
                {departureDate && new Date(departureDate).toLocaleDateString('en-US', { weekday: 'short' })}
                {localTripType === 'roundTrip' && returnDate && (
                  <> - {new Date(returnDate).toLocaleDateString('en-US', { weekday: 'short' })}</>
                )}
              </p>
            </div>

            {/* Travellers */}
            <div className="px-5 border-r border-gray-100">
              <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">Travellers</p>
              <p className="text-base font-bold text-[#1B375B]">
                {String(adults + children + infants).padStart(2, '0')} {adults + children + infants === 1 ? 'Adult' : 'Traveller'}
              </p>
              <p className="text-[10px] text-gray-400">{travelClass}</p>
            </div>

            {/* Modify Button */}
            <div className="pl-5">
              <button 
                onClick={() => setShowModifySearch(true)}
                className="px-6 py-2.5 bg-[#1B375B] text-white rounded-xl text-sm font-bold hover:bg-[#659A9B] transition-all shadow-sm"
              >
                Modify Search
              </button>
            </div>
          </div>
        </div>
      ) : (
        // COMPACT EXPANDED VIEW
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-lg p-4">
          {/* Trip Type Tabs (if enabled) - More compact */}
          {showTripTypeSelector && (
            <div className="flex items-center gap-2 mb-3">
              {[
                { id: 'oneWay', label: 'One Way' },
                { id: 'roundTrip', label: 'Round Trip' },
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setLocalTripType(type.id)}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    localTripType === type.id ? 'bg-[#1B375B] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          )}

          {/* Compact Search Fields */}
          <div className="flex items-stretch gap-2">
            {/* From */}
            <div className="flex-1 px-3 py-2 bg-gray-50 rounded-xl border border-gray-200">
              <AirportPicker label="From" selectedCode={localFromAirport} onSelect={setLocalFromAirport} />
            </div>

            {/* Swap */}
            <div className="flex items-center justify-center">
              <button
                onClick={handleSwapAirports}
                className="w-9 h-9 rounded-full bg-[#1B375B] text-white flex items-center justify-center shadow-md hover:bg-[#659A9B] transition-all"
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* To */}
            <div className="flex-1 px-3 py-2 bg-gray-50 rounded-xl border border-gray-200">
              <AirportPicker label="To" selectedCode={localToAirport} onSelect={setLocalToAirport} />
            </div>

            {/* Departure */}
            <div className="px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 min-w-[130px]">
              <DatePicker label="Departure" date={localDepartureDate} onSelect={setLocalDepartureDate} placeholder="Select" />
            </div>

            {/* Return (if Round Trip) */}
            {localTripType === 'roundTrip' && (
              <div className="px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 min-w-[130px]">
                <DatePicker label="Return" date={localReturnDate} onSelect={setLocalReturnDate} placeholder="Select" minDate={localDepartureDate} />
              </div>
            )}

            {/* Travellers */}
            <div className="px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 min-w-[130px]">
              <PassengerSelector
                adults={localAdults}
                children={localChildren}
                infants={localInfants}
                onChange={({ adults: a, children: c, infants: inf }) => { 
                  setLocalAdults(a); 
                  setLocalChildren(c); 
                  setLocalInfants(inf); 
                }}
              />
            </div>

            {/* Class */}
            <div className="px-3 py-2 bg-gray-50 rounded-xl border border-gray-200 min-w-[120px]">
              <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">Class</p>
              <select
                value={localTravelClass}
                onChange={(e) => setLocalTravelClass(e.target.value)}
                className="text-sm font-bold text-[#1B375B] bg-transparent focus:outline-none cursor-pointer w-full mt-0.5"
              >
                <option value="Economy">Economy</option>
                <option value="Premium Economy">Premium</option>
                <option value="Business">Business</option>
                <option value="First Class">First</option>
              </select>
            </div>
          </div>

          {/* Compact Action Buttons */}
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              onClick={handleCancel}
              className="px-5 py-2 text-gray-600 font-semibold text-sm hover:text-[#1B375B] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSearch}
              className="flex items-center gap-2 px-6 py-2 bg-[#1B375B] text-white rounded-xl text-sm font-bold hover:bg-[#659A9B] transition-all shadow-md"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopModifySearch;
